import React from 'react';

// Base skeleton element with shimmer animation
export function SkeletonBlock({ width = '100%', height = 16, borderRadius = 4, style = {} }) {
  return (
    <div
      className="skeleton-shimmer"
      style={{
        width,
        height,
        borderRadius,
        ...style,
      }}
    />
  );
}

// A line of text placeholder
export function SkeletonLine({ width = '100%', height = 14 }) {
  return <SkeletonBlock width={width} height={height} borderRadius={4} />;
}

// A card-shaped skeleton
function SkeletonCard({ children, style = {} }) {
  return (
    <div className="card" style={style}>
      {children}
    </div>
  );
}

// Metric card skeleton (title + big number + label)
function SkeletonMetricCard() {
  return (
    <SkeletonCard>
      <SkeletonLine width="60%" height={12} />
      <SkeletonBlock width="40%" height={28} borderRadius={4} style={{ marginTop: 12 }} />
      <SkeletonLine width="50%" height={11} />
    </SkeletonCard>
  );
}

// Progress bar skeleton (label + bar)
function SkeletonProgress() {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <SkeletonLine width="30%" height={13} />
        <SkeletonLine width="15%" height={13} />
      </div>
      <SkeletonBlock width="100%" height={8} borderRadius={4} />
    </div>
  );
}

// Table skeleton (header + n rows)
function SkeletonTable({ cols = 5, rows = 4 }) {
  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i}><SkeletonLine width={`${50 + Math.random() * 40}%`} height={12} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><SkeletonLine width={`${40 + Math.random() * 50}%`} height={13} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Service health card skeleton
function SkeletonServiceCard() {
  return (
    <SkeletonCard>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <SkeletonBlock width={36} height={36} borderRadius={8} />
        <div style={{ flex: 1 }}>
          <SkeletonLine width="60%" height={14} />
          <SkeletonLine width="40%" height={12} />
          <SkeletonLine width="75%" height={11} />
        </div>
      </div>
    </SkeletonCard>
  );
}

// ─── Page-specific skeleton layouts ──────────────────────────────

export function DashboardSkeleton() {
  return (
    <div className="fade-in">
      {/* Status banner */}
      <SkeletonCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <SkeletonLine width="30%" height={16} />
          <SkeletonBlock width={90} height={22} borderRadius={12} />
        </div>
        <SkeletonLine width="55%" height={22} />
        <SkeletonLine width="35%" height={12} />
      </SkeletonCard>

      {/* Health cards */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <SkeletonServiceCard />
        <SkeletonServiceCard />
        <SkeletonServiceCard />
        <SkeletonServiceCard />
      </div>

      {/* PM2 + Performance */}
      <div className="grid-2-1" style={{ marginBottom: 24 }}>
        <SkeletonCard>
          <div style={{ marginBottom: 16 }}>
            <SkeletonLine width="40%" height={16} />
          </div>
          <SkeletonTable cols={7} rows={4} />
        </SkeletonCard>
        <SkeletonCard>
          <div style={{ marginBottom: 20 }}>
            <SkeletonLine width="35%" height={16} />
          </div>
          <SkeletonProgress />
          <SkeletonProgress />
          <SkeletonProgress />
        </SkeletonCard>
      </div>

      {/* Backups */}
      <SkeletonCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <SkeletonLine width="35%" height={16} />
          <SkeletonBlock width={80} height={20} borderRadius={12} />
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
            <SkeletonLine width="45%" height={14} />
            <SkeletonLine width="65%" height={11} />
          </div>
        ))}
      </SkeletonCard>
    </div>
  );
}

export function SystemSkeleton() {
  return (
    <div className="fade-in">
      {/* CPU, Memory, Disk */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i}>
            <SkeletonLine width="50%" height={14} />
            <SkeletonBlock width="35%" height={32} borderRadius={4} style={{ marginTop: 12 }} />
            <SkeletonLine width="55%" height={11} />
            <SkeletonBlock width="100%" height={8} borderRadius={4} style={{ marginTop: 12 }} />
            <SkeletonLine width="40%" height={11} />
          </SkeletonCard>
        ))}
      </div>

      {/* MySQL + SSL */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <SkeletonCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <SkeletonLine width="30%" height={16} />
            <SkeletonBlock width={70} height={20} borderRadius={12} />
          </div>
          <SkeletonTable cols={2} rows={6} />
        </SkeletonCard>
        <SkeletonCard>
          <SkeletonLine width="40%" height={16} />
          {[1, 2, 3].map(i => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
              <SkeletonLine width="50%" height={14} />
              <SkeletonLine width="70%" height={11} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <SkeletonLine width="45%" height={12} />
                <SkeletonBlock width={40} height={18} borderRadius={12} />
              </div>
            </div>
          ))}
        </SkeletonCard>
      </div>

      {/* Server info */}
      <SkeletonCard>
        <SkeletonLine width="35%" height={16} />
        <div style={{ display: 'flex', gap: 32, marginTop: 12 }}>
          {[1, 2, 3, 4].map(i => <SkeletonLine key={i} width="120px" height={14} />)}
        </div>
      </SkeletonCard>
    </div>
  );
}

export function BackupsSkeleton() {
  return (
    <div className="fade-in">
      {/* Summary */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <SkeletonMetricCard />
        <SkeletonMetricCard />
        <SkeletonMetricCard />
      </div>

      {/* Backup list */}
      <SkeletonCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <SkeletonLine width="35%" height={16} />
          <SkeletonBlock width={80} height={28} borderRadius={6} />
        </div>
        <SkeletonTable cols={3} rows={5} />
      </SkeletonCard>
    </div>
  );
}

export function StagingControlSkeleton() {
  return (
    <div className="fade-in">
      {/* EC2 Status */}
      <SkeletonCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <SkeletonLine width="35%" height={16} />
          <SkeletonBlock width={70} height={22} borderRadius={12} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <SkeletonLine width="220px" height={14} />
            <SkeletonLine width="160px" height={14} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SkeletonBlock width={100} height={34} borderRadius={6} />
            <SkeletonBlock width={80} height={34} borderRadius={6} />
            <SkeletonBlock width={100} height={34} borderRadius={6} />
          </div>
        </div>
      </SkeletonCard>

      {/* PM2 */}
      <SkeletonCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <SkeletonLine width="35%" height={16} />
          <SkeletonBlock width={110} height={28} borderRadius={6} />
        </div>
        <SkeletonTable cols={4} rows={3} />
      </SkeletonCard>

      {/* Sync + Backups */}
      <SkeletonCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <SkeletonLine width="35%" height={16} />
          <SkeletonBlock width={48} height={26} borderRadius={13} />
        </div>
        <SkeletonLine width="65%" height={13} />
      </SkeletonCard>

      <SkeletonCard>
        <SkeletonLine width="45%" height={16} />
        {[1, 2, 3].map(i => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
            <div>
              <SkeletonLine width="180px" height={14} />
              <SkeletonLine width="250px" height={11} />
            </div>
            <SkeletonBlock width={80} height={28} borderRadius={6} />
          </div>
        ))}
      </SkeletonCard>
    </div>
  );
}

export function LogsSkeleton() {
  return (
    <div className="fade-in">
      {/* Controls */}
      <SkeletonCard style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <SkeletonLine width="120px" height={16} />
          <SkeletonBlock width={200} height={32} borderRadius={8} />
          <div style={{ flex: 1 }} />
          <SkeletonBlock width={90} height={28} borderRadius={6} />
        </div>
      </SkeletonCard>

      {/* Stdout */}
      <SkeletonCard style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <SkeletonLine width="30%" height={16} />
          <SkeletonBlock width={65} height={18} borderRadius={12} />
        </div>
        <SkeletonBlock width="100%" height={200} borderRadius={6} />
      </SkeletonCard>

      {/* Stderr */}
      <SkeletonCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <SkeletonLine width="30%" height={16} />
          <SkeletonBlock width={65} height={18} borderRadius={12} />
        </div>
        <SkeletonBlock width="100%" height={120} borderRadius={6} />
      </SkeletonCard>
    </div>
  );
}

export function DeploymentSkeleton() {
  return (
    <div className="fade-in">
      {/* Title */}
      <SkeletonLine width="40%" height={22} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginTop: 20, marginBottom: 24, borderBottom: '2px solid var(--border-color)' }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ padding: '10px 20px' }}>
            <SkeletonLine width="80px" height={14} />
          </div>
        ))}
      </div>

      {/* Version comparison table */}
      <SkeletonCard style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <SkeletonLine width="40%" height={16} />
        </div>
        <SkeletonTable cols={6} rows={3} />
        <div style={{ marginTop: 12 }}>
          <SkeletonLine width="70%" height={12} />
        </div>
      </SkeletonCard>
    </div>
  );
}
